import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Typography, TextField, Button, Card, CardContent, Avatar, Box, Divider, Dialog, DialogTitle, DialogContent, DialogActions } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import DeleteIcon from "@material-ui/icons/Delete";
import { useDispatch } from "react-redux";

import useStyles from "../styles";
import { commentCourse, getCourseComments, deleteCommentAction } from "../../../actions/courses";

const useLocalStyles = makeStyles((theme) => ({
  commentsContainer: {
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === 'dark' ? '#2e2e2e' : '#f9f9f9',
    borderRadius: "8px",
    [theme.breakpoints.down('xs')]: {
      backgroundColor: 'transparent',
      padding: 0,
    },
  },
  commentsList: {
    marginBottom: theme.spacing(3),
    maxHeight: "400px",
    overflowY: "auto",
    paddingRight: theme.spacing(1),
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: theme.palette.type === 'dark' ? '#2c2c2c' : '#f1f1f1',
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.type === 'dark' ? '#5c5c5c' : '#ccc',
      borderRadius: "4px",
      "&:hover": {
        backgroundColor: theme.palette.type === 'dark' ? '#6c6c6c' : '#999',
      },
    },
  },
  commentCard: {
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.type === 'dark' ? '#424242' : '#fff',
    borderLeft: `4px solid ${theme.palette.primary.main}`,
  },
  commentHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  commentAuthorBox: {
    display: "flex",
    alignItems: "center",
  },
  commentAuthor: {
    fontWeight: "bold",
    marginRight: theme.spacing(1),
  },
  commentTime: {
    fontSize: "0.85rem",
    color: theme.palette.type === 'dark' ? '#bbb' : '#767676',
  },
  commentImage: {
    maxWidth: "100%",
    maxHeight: "300px",
    marginTop: theme.spacing(1),
    borderRadius: "4px",
  },
  inputSection: {
    backgroundColor: theme.palette.type === 'dark' ? '#424242' : '#fff',
    padding: theme.spacing(2),
    borderRadius: "8px",
    border: `1px solid ${theme.palette.divider}`,
  },
  textField: {
    marginBottom: theme.spacing(2),
  },
  loginPrompt: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(3),
    backgroundColor: theme.palette.type === 'dark' ? '#424242' : '#fff',
    borderRadius: "8px",
  },
  buttonGroup: {
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "flex-end",
  },
  deleteButton: {
    color: theme.palette.error.main,
    "&:hover": {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(244, 67, 54, 0.08)' : 'rgba(244, 67, 54, 0.04)',
    },
  },
}));

const CourseCommentSection = ({ course }) => {
  const classes = useStyles();
  const localClasses = useLocalStyles();
  const dispatch = useDispatch();
  const [comments, setComments] = useState([]);
  const [comment, setComment] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [commentIdToDelete, setCommentIdToDelete] = useState(null);
  const commentRef = useRef();
  const user = JSON.parse(localStorage.getItem("profile"));

  useEffect(() => {
    let isMounted = true;

    const fetchComments = async () => {
      if (!course || !course._id) {
        return;
      }
      
      const courseComments = await dispatch(getCourseComments(course._id));
      if (isMounted) {
        setComments(courseComments || []);
      }
    };
    
    fetchComments();

    return () => {
      isMounted = false;
    };
  }, [course._id, dispatch]);



  const handleSubmit = async () => {
    setLoading(true);
    const finalComment = `${user.result.name}: ${comment}`;

    const formData = new FormData();
    formData.append("value", finalComment);

    await dispatch(commentCourse(course._id, formData));

    const courseComments = await dispatch(getCourseComments(course._id));
    setComments(courseComments || []);
    setComment("");
    setLoading(false);
  };

  const handleDeleteComment = (commentId) => {
    setCommentIdToDelete(commentId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (commentIdToDelete) {
      await dispatch(deleteCommentAction(course._id, commentIdToDelete));
      const courseComments = await dispatch(getCourseComments(course._id));
      setComments(courseComments || []);
      setDeleteConfirmOpen(false);
      setCommentIdToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setCommentIdToDelete(null);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  return (
    <>
      <Typography variant="h6" style={{ paddingBottom: '16px', marginTop: 64 }}>
        Comments ({comments.length})
      </Typography>
      <Box className={localClasses.commentsContainer}>
        {comments.length > 0 ? (
        <Box className={localClasses.commentsList}>
          {comments.map((comment, index) => {
            const authorName = comment.comment.split(":")[0];
            const commentText = comment.comment.split(":").slice(1).join(":").trim();
            const isOwnComment = user?.result?.name === authorName;

            return (
              <Card key={index} className={localClasses.commentCard}>
                <CardContent>
                  <Box className={localClasses.commentHeader}>
                    <Box className={localClasses.commentAuthorBox}>
                      <Avatar style={{ width: 32, height: 32, marginRight: "8px" }}>
                        {authorName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box>
                        <Typography className={localClasses.commentAuthor} variant="body2">
                          {authorName}
                        </Typography>
                        <Typography className={localClasses.commentTime} variant="caption">
                          {comment.createdAt ? formatDate(comment.createdAt) : "just now"}
                        </Typography>
                      </Box>
                    </Box>
                    {isOwnComment && (
                      <Button
                        size="small"
                        className={localClasses.deleteButton}
                        startIcon={<DeleteIcon />}
                        onClick={() => handleDeleteComment(comment.commentId)}
                      />
                    )}
                  </Box>
                  <Typography variant="body2" style={{ marginTop: "8px", lineHeight: 1.6 }}>
                    {commentText}
                  </Typography>
                  {comment.image && (
                    <img
                      src={comment.image}
                      alt="Comment attachment"
                      className={localClasses.commentImage}
                    />
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Box>
      ) : (
        <Box style={{ padding: '12px', textAlign: 'center', backgroundColor: 'inherit', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.12)', marginBottom: 16 }}>
          <Typography variant="body2" color="textSecondary">
            No comments yet. Be the first to comment!
          </Typography>
        </Box>
      )}

      {user?.result?.name ? (
        <Box className={localClasses.inputSection}>
          <Typography variant="subtitle1" gutterBottom style={{ fontWeight: "bold" }}>
            Add a Comment
          </Typography>
          <TextField
            className={localClasses.textField}
            fullWidth
            minRows={3}
            variant="outlined"
            multiline
            placeholder="Share your thoughts about this course..."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />

          <Box className={localClasses.buttonGroup}>
            <Button
              variant="contained"
              color="primary"
              disabled={!comment.trim() || loading}
              onClick={handleSubmit}
            >
              {loading ? "Posting..." : "Post Comment"}
            </Button>
          </Box>
        </Box>
      ) : (
        <Box className={localClasses.loginPrompt}>
          <Typography variant="body2" color="textSecondary">
            <Typography
              component={Link}
              to="/auth"
              className={classes.logout}
              variant="inherit"
              color="primary"
            >
              Sign in
            </Typography>
            {" "}to add a comment
          </Typography>
        </Box>
      )}
      <div ref={commentRef} />

      <Dialog open={deleteConfirmOpen} onClose={handleCancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Comment</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to delete this comment? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="default">
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} color="secondary" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </>
  );
};

export default CourseCommentSection;
